import { Policy } from "@repo/api/schemas/policies.schema";
import {
  ButtonIcon,
  Icon,
  Menu,
  type MenuOption,
} from "@saviynt/design-system";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { getKebabMenuList } from "./ActionButton.constants";
import PolicyActionModal, {
  PolicyActionVariant,
} from "../PolicyActionModal/PolicyActionModal";
import {
  useDeletePolicy,
  useUpdateClonePolicy,
} from "../../../hooks/usePolicy";
import { useNavigate } from "@tanstack/react-router";

interface ActionButtonProps {
  rowData: Policy;
}

export default function ActionButton({ rowData }: ActionButtonProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { id: policyId } = rowData;
  const [modalVariant, setModalVariant] = useState<PolicyActionVariant | null>(
    null,
  );
  const menuOptions = useMemo(
    () => getKebabMenuList({ intl, rowData }),
    [intl, rowData],
  );

  const deletePolicyMutation = useDeletePolicy({
    onError: (message) => {
      console.error(message);
    },
    onSuccess: () => {
      setModalVariant(null);
    },
  });
  const disablePolicyMutation = useUpdateClonePolicy({
    onError: (message) => {
      console.error(message);
    },
    onSuccess: () => {
      setModalVariant(null);
    },
  });

  const handleKebabMenuClick = (selectedOption: MenuOption) => {
    if (
      selectedOption.value === "delete" ||
      selectedOption.value === "disable" ||
      selectedOption.value === "enable"
    ) {
      setModalVariant(selectedOption.value);
    }

    if (selectedOption.value === "edit" || selectedOption.value === "clone") {
      void navigate({
        to: "/edit-policy/$policyId",
        params: { policyId },
        search: { action: selectedOption.value },
      });
    }
  };

  return (
    <>
      <Menu
        isPortalEnabled
        options={menuOptions}
        onChange={handleKebabMenuClick}
        trigger={
          <ButtonIcon
            size="medium"
            kind="ghost"
            ariaLabel={intl.formatMessage({
              defaultMessage: "Policy actions",
              id: "Qn7bg7",
              description: "Aria label for action button in policy list page",
            })}
            icon={<Icon kind="MoreVertical" size="medium" />}
          />
        }
      />
      {modalVariant && (
        <PolicyActionModal
          variant={modalVariant}
          isOpen={!!modalVariant}
          setIsOpen={(open) => !open && setModalVariant(null)}
          onConfirm={() => {
            if (modalVariant === "delete") {
              deletePolicyMutation.mutate(policyId);
            }
            if (modalVariant === "disable") {
              disablePolicyMutation.mutate({
                policyId,
                actionType: "edit",
                payload: {
                  is_active: false,
                },
              });
            }
            if (modalVariant === "enable") {
              disablePolicyMutation.mutate({
                policyId,
                actionType: "edit",
                payload: {
                  is_active: true,
                },
              });
            }
          }}
        />
      )}
    </>
  );
}
